<template lang="pug">
    div
        template(v-if="loading")
            .text-center
                v-progress-circular(
                    :size='100'
                    :width='10'
                    color='var(--volie-blue)'
                    indeterminate
                    style="margin-top: 75px; margin-bottom: 75px;"
                )

        template(v-else)
            v-card.d-flex.flex-column.ma-5
                .summary.pa-5
                    v-list.pa-0
                        v-list-item.d-flex.justify-space-between.align-center.px-0
                            v-list-item-title(style="font-weight: 500;")
                                div(:class="pulseAssessmentClass")
                                    | {{ pulseAssessment }}
                            template(v-if="renderSharedLinks")
                                SharedLinksDialog(
                                    :account-id="llmAssessment.account_id"
                                    component="pulse_assessment_show"
                                    :shareable-id="llmAssessment.llm_assessment_id"
                                    shareable-type="pulse_assessment"
                                )
                    v-card-text.pa-0(v-if="summaryLlmAssessmentItem")
                        LlmAssessmentItem(
                            :llm-assessment-item="summaryLlmAssessmentItem"
                            :hide-user-feedback="sourceIsSharedLink"
                        )
                    template(v-else)
                        v-alert(outlined dense border='left')
                            | No summary for this assessment.

                .assessment-info-container
                    v-card-text.assessment-items
                        v-card(outlined)
                            template(v-if="validLlmAssessmentItems")
                                v-list
                                    v-list-item(v-for="llmAssessmentItem in llmAssessmentItems" :key="llmAssessmentItem.llm_assessment_item_id")
                                        LlmAssessmentItem(
                                            :llm-assessment="llmAssessment"
                                            :llm-assessment-item="llmAssessmentItem"
                                            :hide-user-feedback="sourceIsSharedLink"
                                        )
                            template(v-else)
                                v-alert(outlined dense border='left')
                                    | No assessment items found.
                    v-card-text.communication-details
                        CommunicationDetailsCard(
                            title="Communication Details"
                            :communication-type="llmAssessment.communication_type"
                            :hide-conference-link="sourceIsSharedLink"
                            :status="llmAssessment.status"
                            :scorecard="llmAssessment"
                            :organization-names="llmAssessment.organization_name ? [llmAssessment.organization_name] : []"
                            :campaign-names="llmAssessment.campaign_name ? [llmAssessment.campaign_name] : []"
                            :campaign-type-names="llmAssessment.campaign_type_names"
                            :disposition-descriptions="llmAssessment.disposition_description ? [llmAssessment.disposition_description] : []"
                            :user-display-names="llmAssessment.user_display_name ? [llmAssessment.user_display_name] : []"
                            readonly
                        )   
                        CallRecordingAndTranscriptCard(
                            :scorecard="llmAssessment"
                            :hide-download-buttons="hideDownloadButtons"
                        )
                    template(v-if="hasErrorMessage")
                        v-alert.ml-2(outlined dense type='error' border='left')
                            | {{ caughtErrorMessage }}
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Components
import LlmAssessmentItem from "@/components/scorecards/llm_assessment_item.vue";
import SharedLinksDialog from '@/components/shared_links/dialog.vue';
import CallRecordingAndTranscriptCard from "@/components/scorecards/communication_detail.vue";
import CommunicationDetailsCard from '@/components/scorecard_criterions/card.vue';

// Data
import { FindLlmAssessment } from '@/data';

export default Vue.extend({
    data() {
        return {
            caughtErrorMessage: null,
            llmAssessment: null,
            llmAssessmentItems: [],
            loading: false,
            summaryLlmAssessmentItem: null,
        }
    },
    methods: {
        findLlmAssessment() {
            let vid = ""
            
            if (this.sourceIsSharedLink) {
                vid = this.llmAssessmentVid;
            } else {
                vid = this.$route?.params?.vid;
            }

            if (!(vid?.length > 0)) {
                return;
            }

            this.loading = true;
            this.caughtErrorMessage = null;

            FindLlmAssessment(vid).then((response) => {
                if (response?.Data?.llm_assessment) {
                    this.llmAssessment = response.Data.llm_assessment;

                    // handle llm_assessment_items
                    if (response.Data.llm_assessment_items?.length > 0) {
                        const items = response.Data.llm_assessment_items;
                        
                        // grab summary item
                        this.summaryLlmAssessmentItem = items.find(
                            item => item.friendly_llm_assessment_type === 'Summary'
                        ) || null;

                        // all other items
                        this.llmAssessmentItems = items.filter(
                            item => item.friendly_llm_assessment_type !== 'Summary'
                        );
                    } else {
                        this.llmAssessmentItems = [];
                    }
                }
            }).catch(() => {
                this.caughtErrorMessage = "Could not find Pulse assessment.";
            }).finally(() => {
                this.loading = false;
            });
        },
    },
    computed: {
        currentUser() {
            return VolieState.currentUser();
        },
        hasErrorMessage() {
            return this.caughtErrorMessage;
        },
        pulseAssessment() {
            if (!this.llmAssessment) {
                return "Pulse Assessment";
            }

            return this.llmAssessment?.is_flagged ? "Flagged by Pulse" : "Approved by Pulse";
        },
        pulseAssessmentClass() {
            if (this.pulseAssessment === "Approved by Pulse") {
                return "by-pulse-banner approved-by-pulse-banner";
            }
            if (this.pulseAssessment === "Flagged by Pulse") {
                return "by-pulse-banner flagged-by-pulse-banner";
            }
            return "";
        },
        renderSharedLinks() {
            return this.currentUser?.account?.shared_links_enabled === true && !this.sourceIsSharedLink;
        },
        sourceIsSharedLink() {
            return this.source === 'shared_link';
        },
        validLlmAssessmentItems() {
            return this.llmAssessmentItems?.length > 0;
        },
        validLlmAssessmentSummary() {
            return this.summaryLlmAssessmentItem?.llm_assessment_item_id > 0;
        },
    },
    components: {
        CallRecordingAndTranscriptCard,
        CommunicationDetailsCard,
        LlmAssessmentItem,
        SharedLinksDialog,
    },
    props: {
        hideDownloadButtons: {
            type: Boolean,
            default: false,
            required: false,
        },
        llmAssessmentVid: {
            type: String,
            required: false,
        },
        source: {
            type: String,
            required: false,
        },
    },
    watch: {

    },
    created() {
    },
    destroyed() {
    },
    mounted() {
        this.findLlmAssessment();
    },
})
</script>

<style lang="scss" scoped>
.assessment-info-container {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: flex-start;

    .assessment-items,
    .communication-details {
        display: flex;
        flex-direction: column;
        flex: 1 1 0;
        min-height: 0;
        }

    .communication-details {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
}
</style>